import type { FC } from 'react';
import {
  ButtonBase,
  CircularProgress,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';

export interface CardPinFieldViewProps {
  revealed: boolean;
  pin: string | undefined;
  loading: boolean;
  error: boolean;
  onToggle: () => void;
}

const MASKED_PIN = '••••';

export const CardPinFieldView: FC<CardPinFieldViewProps> = ({
  revealed,
  pin,
  loading,
  error,
  onToggle,
}) => {
  const { t } = useTranslation();
  const actionLabel = t(revealed ? 'myCards.pin.hide' : 'myCards.pin.show');

  return (
    <Tooltip title={actionLabel}>
      <PinButton
        onClick={onToggle}
        disabled={loading}
        aria-label={actionLabel}
        data-cy="card-pin-toggle"
      >
        {error ? (
          <Typography sx={{ fontSize: '0.875rem', color: 'error.main' }}>
            {t('myCards.pin.error')}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 500,
              fontFamily: 'monospace',
              letterSpacing: 2,
            }}
          >
            {revealed && pin !== undefined ? pin : MASKED_PIN}
          </Typography>
        )}
        {loading ? (
          <CircularProgress size={16} />
        ) : revealed && !error ? (
          <VisibilityOffIcon fontSize="small" color="primary" />
        ) : (
          <VisibilityIcon fontSize="small" color="primary" />
        )}
      </PinButton>
    </Tooltip>
  );
};

const PinButton = styled(ButtonBase)(({ theme }) =>
  theme.unstable_sx({
    gap: 1,
    px: 1,
    mx: -1,
    borderRadius: 1,
    justifyContent: 'flex-start',
    '&:hover': {
      bgcolor: 'action.hover',
    },
  })
);
